import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, FileText, Scale } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { SiteBreadcrumbs } from "@/components/marketing/SiteBreadcrumbs";
import { useLocale, localizedPath, alternateLinkList } from "@/lib/i18n/locale";
import { useCurrency } from "@/lib/ecom/currency";

export const Route = createFileRoute("/legal/")({
  head: () => ({
    meta: [
      { title: "Legal — banana.cards" },
      { name: "description", content: "All Banana Cards legal documents in one place: Terms, Privacy, Ticketing Terms, Security, and Guidelines." },
      { property: "og:title", content: "Legal — banana.cards" },
      { property: "og:description", content: "Browse all Banana Cards legal documents and policies." },
    ],
    links: alternateLinkList("/legal", "en"),
  }),
  component: LegalHubPage,
});

type LegalDoc = {
  to: string;
  title: string;
  description: string;
  titleVi?: string;
  descriptionVi?: string;
  group: "Platform" | "Ticketing" | "Compliance" | "Partners" | "Trust";
  variant?: "global" | "vietnam";
};

const DOCS: LegalDoc[] = [
  { to: "/terms", title: "Terms of Service", titleVi: "Điều khoản dịch vụ", description: "The rules for using Banana Cards products and services.", descriptionVi: "Quy định sử dụng các sản phẩm và dịch vụ Banana Cards.", group: "Platform" },
  { to: "/privacy", title: "Privacy Policy", titleVi: "Chính sách bảo mật", description: "How we collect, use, store, and protect your personal data.", descriptionVi: "Cách chúng tôi thu thập, sử dụng, lưu trữ và bảo vệ dữ liệu cá nhân của bạn.", group: "Platform" },
  { to: "/legal-guidelines", title: "Legal & Ethical Use Guidelines", titleVi: "Hướng dẫn sử dụng hợp pháp & đạo đức", description: "Acceptable use, prohibited activities, and AI usage policy.", descriptionVi: "Sử dụng được chấp nhận, hoạt động bị cấm và chính sách sử dụng AI.", group: "Platform" },
  { to: "/legal/ticketing-global", title: "Ticketing Terms", titleVi: "Điều khoản vé sự kiện", description: "Ticketing services for organisers outside Vietnam (Six Studio Creative FZCO).", descriptionVi: "Dịch vụ vé cho nhà tổ chức ngoài Việt Nam (Six Studio Creative FZCO).", group: "Ticketing", variant: "global" },
  { to: "/legal/ticketing-vietnam", title: "Ticketing Terms", titleVi: "Điều khoản vé sự kiện", description: "Ticketing services for organisers in Vietnam (Banana Cards Vietnam).", descriptionVi: "Dịch vụ vé cho nhà tổ chức tại Việt Nam (Banana Cards Vietnam).", group: "Ticketing", variant: "vietnam" },
  { to: "/legal/kyc-global", title: "KYC & Verification", titleVi: "KYC & Xác minh", description: "Know Your Customer, Know Your Business, beneficial ownership and verification for global organisers.", descriptionVi: "Nhận biết khách hàng, nhận biết doanh nghiệp, chủ sở hữu hưởng lợi và xác minh cho nhà tổ chức toàn cầu.", group: "Compliance", variant: "global" },
  { to: "/legal/kyc-vietnam", title: "KYC & Verification", titleVi: "KYC & Xác minh", description: "Nhận biết khách hàng, xác minh doanh nghiệp và chủ sở hữu hưởng lợi tại Việt Nam.", descriptionVi: "Nhận biết khách hàng, xác minh doanh nghiệp và chủ sở hữu hưởng lợi tại Việt Nam.", group: "Compliance", variant: "vietnam" },
  { to: "/legal/aml-global", title: "AML, Sanctions & Financial Crime", titleVi: "AML, Cấm vận & Tội phạm tài chính", description: "Anti-money laundering, counter-terrorist financing and sanctions compliance for global organisers.", descriptionVi: "Phòng chống rửa tiền, chống tài trợ khủng bố và tuân thủ cấm vận cho nhà tổ chức toàn cầu.", group: "Compliance", variant: "global" },
  { to: "/legal/aml-vietnam", title: "AML & Financial Crime", titleVi: "AML & Tội phạm tài chính", description: "Phòng chống rửa tiền, chống tài trợ khủng bố và tội phạm tài chính tại Việt Nam.", descriptionVi: "Phòng chống rửa tiền, chống tài trợ khủng bố và tội phạm tài chính tại Việt Nam.", group: "Compliance", variant: "vietnam" },
  { to: "/legal/affiliate-global", title: "Banana Partners Affiliate Program", titleVi: "Chương trình Đối tác Affiliate Banana", description: "Terms & conditions for global affiliate partners promoting Banana Cards online.", descriptionVi: "Điều khoản dành cho đối tác affiliate toàn cầu quảng bá Banana Cards trực tuyến.", group: "Partners", variant: "global" },
  { to: "/legal/affiliate-vietnam", title: "Banana Partners Affiliate Program", titleVi: "Chương trình Đối tác Affiliate Banana", description: "Điều khoản chương trình đối tác tiếp thị Banana Cards tại Việt Nam.", descriptionVi: "Điều khoản chương trình đối tác tiếp thị Banana Cards tại Việt Nam.", group: "Partners", variant: "vietnam" },
  { to: "/legal/retailer-global", title: "Retailer Agreement", titleVi: "Hợp đồng Đại lý", description: "Consignment-based retail partner agreement for global resellers of Banana Cards hardware.", descriptionVi: "Hợp đồng đối tác bán lẻ theo hình thức ký gửi cho đại lý toàn cầu của phần cứng Banana Cards.", group: "Partners", variant: "global" },
  { to: "/legal/retailer-vietnam", title: "Retailer Agreement", titleVi: "Hợp đồng Đại lý", description: "Hợp đồng đối tác bán lẻ hình thức ký gửi cho đại lý tại Việt Nam.", descriptionVi: "Hợp đồng đối tác bán lẻ hình thức ký gửi cho đại lý tại Việt Nam.", group: "Partners", variant: "vietnam" },
  { to: "/security", title: "Security", titleVi: "Bảo mật", description: "How we protect your account, data, and infrastructure.", descriptionVi: "Cách chúng tôi bảo vệ tài khoản, dữ liệu và hạ tầng của bạn.", group: "Trust" },
  { to: "/legal/anti-spam", title: "Anti-Spam Policy", titleVi: "Chính sách Chống thư rác", description: "Email conduct, prohibited content and enforcement for messages sent via Banana Cards.", descriptionVi: "Quy tắc gửi email, nội dung bị cấm và biện pháp xử lý đối với tin nhắn gửi qua Banana Cards.", group: "Trust" },
  { to: "/legal/returns", title: "Returns, Refund & Warranty Policy", titleVi: "Chính sách Đổi trả, Hoàn tiền & Bảo hành", description: "Return windows, refund eligibility, warranty and NFC troubleshooting for Banana Cards products.", descriptionVi: "Thời hạn đổi trả, điều kiện hoàn tiền, bảo hành và xử lý sự cố NFC cho sản phẩm Banana Cards.", group: "Trust" },
];

const GROUPS: LegalDoc["group"][] = ["Platform", "Ticketing", "Compliance", "Partners", "Trust"];

const GROUP_LABELS_VI: Record<LegalDoc["group"], string> = {
  Platform: "Nền tảng",
  Ticketing: "Bán vé",
  Compliance: "Tuân thủ",
  Partners: "Đối tác",
  Trust: "Tin cậy",
};

const T = {
  en: {
    eyebrow: "Legal hub",
    h1a: "All our ",
    h1b: "legal documents.",
    intro: "Every policy, agreement, and set of terms that govern how Banana Cards works — for users, organisers, and partners.",
    questions: "Questions?",
    needClar: "Need clarification on any policy?",
    talkToUs: "Talk to us.",
    emailLine1: "Email ",
    emailLine2: " — we read every message.",
  },
  vi: {
    eyebrow: "Trung tâm pháp lý",
    h1a: "Tất cả ",
    h1b: "tài liệu pháp lý.",
    intro: "Mọi chính sách, thỏa thuận và điều khoản chi phối cách Banana Cards hoạt động — dành cho người dùng, nhà tổ chức và đối tác.",
    questions: "Có câu hỏi?",
    needClar: "Cần làm rõ bất kỳ chính sách nào?",
    talkToUs: "Liên hệ với chúng tôi.",
    emailLine1: "Gửi email tới ",
    emailLine2: " — chúng tôi đọc mọi tin nhắn.",
  },
  el: {
    eyebrow: "Νομικό κέντρο",
    h1a: "Όλα τα ",
    h1b: "νομικά έγγραφα.",
    intro: "Κάθε πολιτική, συμφωνία και σύνολο όρων που διέπουν τη λειτουργία των Banana Cards — για χρήστες, διοργανωτές και συνεργάτες.",
    questions: "Ερωτήσεις;",
    needClar: "Χρειάζεστε διευκρίνιση για κάποια πολιτική;",
    talkToUs: "Μιλήστε μας.",
    emailLine1: "Στείλτε email στο ",
    emailLine2: " — διαβάζουμε κάθε μήνυμα.",
  },
};

export function LegalHubPage() {
  const locale = useLocale();
  const { country } = useCurrency();
  const wantVariant: "global" | "vietnam" = country.code === "VN" ? "vietnam" : "global";
  const visibleDocs = DOCS.filter((d) => !d.variant || d.variant === wantVariant);
  const t = T[locale];
  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 pb-10 pt-20 md:px-6">
          <SiteBreadcrumbs className="mb-6" currentName={locale === "vi" ? "Pháp lý" : "Legal"} />
          <p className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <Scale className="h-3.5 w-3.5" />
            {t.eyebrow}
          </p>
          <h1 className="mt-4 font-display text-4xl leading-[1.05] md:text-6xl">
            {t.h1a}<em className="italic">{t.h1b}</em>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
            {t.intro}
          </p>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-5xl px-4 py-16 md:px-6">
          <div className="space-y-12">
            {GROUPS.map((group) => {
              const items = visibleDocs.filter((d) => d.group === group);
              if (items.length === 0) return null;
              return (
                <div key={group}>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{locale === "vi" ? GROUP_LABELS_VI[group] : group}</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {items.map((doc) => (
                      <Link
                        key={doc.to}
                        to={localizedPath(doc.to, locale)}
                        className="group flex items-start justify-between gap-4 rounded-2xl bg-card p-6 transition hover:bg-foreground hover:text-background"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 opacity-60" />
                            <h2 className="font-display text-lg font-bold leading-tight">{locale === "vi" && doc.titleVi ? doc.titleVi : doc.title}</h2>
                          </div>
                          <p className="mt-2 text-sm leading-relaxed text-muted-foreground transition group-hover:text-background/80">
                            {locale === "vi" && doc.descriptionVi ? doc.descriptionVi : doc.description}
                          </p>
                        </div>
                        <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 transition group-hover:rotate-12" />
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 rounded-2xl bg-card p-6 md:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t.questions}</p>
            <h3 className="mt-2 font-display text-xl md:text-2xl">
              {t.needClar} <em className="italic">{t.talkToUs}</em>
            </h3>
            <p className="mt-3 text-sm text-muted-foreground">
              {t.emailLine1}
              <a href="mailto:hello@banana.cards" className="font-bold text-foreground underline-offset-4 hover:underline">
                hello@banana.cards
              </a>
              {t.emailLine2}
            </p>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}